import { Form } from 'antd';
import BaseModal from 'basic-ui-lib/components/Modal/BaseModal';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { setPageLoading } from 'widgets/PageLoading';
import { toast } from 'react-toastify';
import { getMessageError } from 'services/response';
import InputLarge from 'basic-ui-lib/components/Input/InputLarge';
import { useMediaQuery } from 'react-responsive';
import BaseInput from '../Input/BaseInput';
import { Colors } from 'basic-ui-lib';

interface EditNameModalProps {
  currentName: string;
  id: string;
  onFinish?: (id: string, name: string) => Promise<void> | void;
  modalTitle?: string;
}

export interface EditNameModalRef {
  open: ({ currentName, id, onFinish, modalTitle }: EditNameModalProps) => void;
}
interface Props {}

interface FormValues {
  name: string;
}

const EditNameModal = forwardRef<EditNameModalRef, Props>(({}, ref) => {
  const [form] = Form.useForm();
  const [isOpen, setIsOpen] = useState(false);
  const [currentData, setCurrentData] = useState<EditNameModalProps | null>(null);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const onFinishRef = useRef<((id: string, name: string) => Promise<void> | void) | null>(null);

  useImperativeHandle(ref, () => ({
    open: ({ currentName, id, onFinish, modalTitle }: EditNameModalProps) => {
      setCurrentData({ currentName, id, modalTitle });
      onFinishRef.current = onFinish || null;
      setIsOpen(true);
      form.setFieldsValue({ name: currentName });
    },
  }));

  const onClose = () => {
    setIsOpen(false);
    setCurrentData(null);
    onFinishRef.current = null;
    form.resetFields();
  };

  const handleSubmit = () => {
    form.submit();
  };

  const handleFinish = async (values: FormValues) => {
    if (!currentData) return;

    try {
      setPageLoading(true);

      if (onFinishRef.current) {
        await onFinishRef.current(currentData.id, values.name);
      }
      onClose();
    } catch (error) {
      toast.error(getMessageError(error) || 'An error occurred while updating the name.');
    } finally {
      setPageLoading(false);
    }
  };

  if (isMobile) {
    return (
      <BaseModal title={currentData?.modalTitle || 'Edit Name'} isOpen={isOpen} width={'100%'} onClose={onClose} okText='Ok' onOk={handleSubmit} isMobile={true}>
        <Container>
          <Form layout='vertical' form={form} onFinish={handleFinish}>
            <Form.Item name='name' rules={[{ required: true, message: 'Please input name' }]}>
              <BaseInput placeholder='Please enter name' />
            </Form.Item>
          </Form>
        </Container>
      </BaseModal>
    );
  }

  return (
    <BaseModal title={currentData?.modalTitle || 'Edit Name'} isOpen={isOpen} width={1000} onClose={onClose} okText='Ok' onOk={handleSubmit}>
      <Container>
        <Form layout='vertical' form={form} onFinish={handleFinish}>
          <Form.Item name='name' rules={[{ required: true, message: 'Please input name' }]}>
            <InputLarge placeholder='Please enter name' />
          </Form.Item>
        </Form>
      </Container>
    </BaseModal>
  );
});

EditNameModal.displayName = 'EditNameModal';
export default EditNameModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding: 0 50px;

  @media (max-width: 768px) {
    padding: 16px 16px 32px;
    background: ${Colors.neutral.bg[2]};
  }

  .ant-form-item {
    margin-bottom: 0;
  }
`;
